// src/components/ICTData/ICTDetails.js

import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchICTData } from '../../../store/uiSlice';

// Study Modes
import {
  fetchAllStudyModes,
  updateStudyModeStatus,
  insertStudyMode,
} from '../../../store/studyModeSlice';

// Benefits
import {
  fetchBenefitsByICTId,
  updateBenefitDesc,
  deleteBenefit,
  insertBenefit,
} from '../../../store/benefitsSlice';

// Hours (now stored in a dictionary keyed by ictId)
import {
  fetchHoursByICTId,
  insertHour,
  deleteHour,
  updateHour,
} from '../../../store/hoursSlice';

// ICT Costs
import {
  fetchIctCostByIctId,
  insertIctCost,
  updateIctCost,
  deleteIctCost,
} from '../../../store/ictCostSlice';

import { fetchTrainersByIctId } from '../../../store/ictTrainersByIctIdSlice';
import { fetchEmployees } from "../../../store/empDataSlice";

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../../Styles/Common.css';

const ICTDetails = () => {
  const dispatch = useDispatch();

  // --- Redux Slices ---
  const ictData = useSelector((state) => state.ui.ictData || []);
  const { employees = [] } = useSelector((state) => state.empData);
  
  // Study Modes
  const studyModes = useSelector((state) => state.studyModes.studyModes || []);
  const studyModesLoading = useSelector((state) => state.studyModes.loading);

  // Benefits
  const benefits = useSelector((state) => state.benefits.benefits || []);
  const benefitsLoading = useSelector((state) => state.benefits.loading);

  // Hours dictionary: hoursByIct = { [ictId]: [arrayOfHourRecords], ... }
  const hoursByIct = useSelector((state) => state.hours.hoursByIct || {});
  const hoursLoading = useSelector((state) => state.hours.loading);

  // ICT Costs
  const ictCosts = useSelector((state) => state.ictCost.ictCosts || []);
  const ictCostsLoading = useSelector((state) => state.ictCost.loading);
  const ictCostsError = useSelector((state) => state.ictCost.error);

  // --- Local State ---
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredICTs, setFilteredICTs] = useState([]);
  const [selectedICT, setSelectedICT] = useState(null);

  const [newPrDesc,       setNewPrDesc]       = useState('');
  const [editingPrId,     setEditingPrId]     = useState(null);
  const [editingPrDesc,   setEditingPrDesc]   = useState('');

  // Popups
  const [isStudyModePopupOpen, setStudyModePopupOpen] = useState(false);
  const [isBenefitPopupOpen, setBenefitPopupOpen] = useState(false);
  const [isHourPopupOpen, setHourPopupOpen] = useState(false);
  const [isIctCostPopupOpen, setIctCostPopupOpen] = useState(false);
  const {
      data: trainers = [],
      loading: trainersLoading,
      error: trainersError,
  } = useSelector((state) => state.ictTrainersByIctId);



  // Insert forms
  const [newStudyMode, setNewStudyMode] = useState({
    ictId_id: '',
    studyMode: '',
    studyModeDesc: '',
  });
  const [newBenefit, setNewBenefit] = useState({
    ictId_id: '',
    benefitDesc: '',
    status: '1',
  });
  const [newHour, setNewHour] = useState({
    TrainingHour: '',
    LabHour: '',
    ictId_id: '',
  });
  const [newIctCost, setNewIctCost] = useState({
    ictid: '',
    ictMrp: '',
    cgst: '',
    sgst: '',
    igst: '',
    sellPrice: '',
  });

  // Editing states
  const [editingBenefitId, setEditingBenefitId] = useState(null);
  const [editingBenefitDesc, setEditingBenefitDesc] = useState('');

  const [editingHourId, setEditingHourId] = useState(null);
  const [editingHourData, setEditingHourData] = useState({
    TrainingHour: '',
    LabHour: '',
  });

  const [editingIctCostId, setEditingIctCostId] = useState(null);
  const [editingIctCostData, setEditingIctCostData] = useState({
    ictMrp: '',
    cgst: '',
    sgst: '',
    igst: '',
    sellPrice: '',
  });

  // --- Initial Fetch ---
  useEffect(() => {
    dispatch(fetchICTData());   // Load all ICT records
    dispatch(fetchAllStudyModes());
    dispatch(fetchEmployees());
    // We only fetch benefits/hours/ictCosts after selecting an ICT.
  }, [dispatch]);

  // --- Searching ICTs ---
  useEffect(() => {
    if (searchTerm) {
      const results = ictData.filter((ict) =>
        ict.ictName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredICTs(results);
    } else {
      setFilteredICTs([]);
    }
  }, [searchTerm, ictData]);

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  // --- Handle ICT selection ---
  const handleICTSelect = (ict) => {
    setSelectedICT(ict);

    // Pre-fill new record forms
    setNewStudyMode({ ...newStudyMode, ictId_id: ict.id });
    setNewBenefit({ ...newBenefit, ictId_id: ict.id });
    setNewHour({ ...newHour, ictId_id: ict.id });
    setNewIctCost({ ...newIctCost, ictid: ict.id });

    // Clear search
    setSearchTerm('');
    setFilteredICTs([]);

    // Fetch details for this ICT
    dispatch(fetchBenefitsByICTId(ict.id));
    dispatch(fetchHoursByICTId(ict.id));
    dispatch(fetchIctCostByIctId(ict.id));
    dispatch(fetchTrainersByIctId(ict.id));
  };


  // --- STUDY MODES Panel ---
  const handleStatusToggle = async (id, currentStatus) => {
    const newStatus = currentStatus === 'A' ? 'D' : 'A';
    try {
      await dispatch(updateStudyModeStatus({ id, status: newStatus })).unwrap();
      toast.success(`Status updated to ${newStatus === 'A' ? 'Active' : 'Deactivated'}`);
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleInsertStudyMode = async (e) => {
    e.preventDefault();
    if (!newStudyMode.studyMode || !newStudyMode.studyModeDesc) {
      toast.error('Please fill in all required fields.');
      return;
    }
    const payload = { ...newStudyMode, status: 'A' };
    try {
      await dispatch(insertStudyMode(payload)).unwrap();
      toast.success('Study Mode inserted successfully!');
      setNewStudyMode({
        ictId_id: selectedICT.id,
        studyMode: '',
        studyModeDesc: '',
      });
      setStudyModePopupOpen(false);
    } catch {
      toast.error('Failed to insert Study Mode.');
    }
  };

  // --- BENEFITS Panel ---
  const handleInsertBenefit = async (e) => {
    e.preventDefault();
    if (!newBenefit.benefitDesc) {
      toast.error('Please fill in all required fields.');
      return;
    }
    try {
      await dispatch(insertBenefit(newBenefit)).unwrap();
      toast.success('Benefit inserted successfully!');
      setNewBenefit({
        ictId_id: selectedICT.id,
        benefitDesc: '',
        status: '1',
      });
      setBenefitPopupOpen(false);
    } catch {
      toast.error('Failed to insert Benefit.');
    }
  };

  const handleEditBenefit = (benefit) => {
    setEditingBenefitId(benefit.id);
    setEditingBenefitDesc(benefit.benefitDesc || '');
  };

  const handleSaveBenefit = async () => {
    try {
      await dispatch(
        updateBenefitDesc({
          id: editingBenefitId,
          benefitDesc: editingBenefitDesc,
        })
      ).unwrap();
      toast.success('Benefit updated successfully');
    } catch {
      toast.error('Failed to update benefit');
    } finally {
      setEditingBenefitId(null);
      setEditingBenefitDesc('');
    }
  };

  const handleDeleteBenefit = async (id) => {
    if (window.confirm('Are you sure you want to delete this benefit?')) {
      try {
        await dispatch(deleteBenefit(id)).unwrap();
        toast.success('Benefit deleted successfully');
      } catch {
        toast.error('Failed to delete benefit');
      }
    }
  };

  // --- HOURS Panel ---
  const handleInsertHour = async (e) => {
    e.preventDefault();
    if (!newHour.TrainingHour || !newHour.LabHour) {
      toast.error('Please fill in all required fields.');
      return;
    }
    try {
      const result = await dispatch(insertHour(newHour)).unwrap();
      toast.success('Hour data inserted successfully!');
      setNewHour({ TrainingHour: '', LabHour: '', ictId_id: selectedICT.id });
      setHourPopupOpen(false);
    } catch {
      toast.error('Failed to insert hour data.');
    }
  };

  const handleEditHour = (hour) => {
    setEditingHourId(hour.id);
    setEditingHourData({
      TrainingHour: String(hour.TrainingHour || ''),
      LabHour: String(hour.LabHour || ''),
    });
  };

  const handleSaveHour = async () => {
    try {
      const payload = {
        id: editingHourId,
        TrainingHour: editingHourData.TrainingHour,
        LabHour: editingHourData.LabHour,
      };
      await dispatch(updateHour(payload)).unwrap();
      toast.success('Hour data updated successfully!');
    } catch {
      toast.error('Failed to update hour data.');
    } finally {
      setEditingHourId(null);
      setEditingHourData({ TrainingHour: '', LabHour: '' });
    }
  };

  const handleDeleteHour = async (id) => {
    if (window.confirm('Are you sure you want to delete this hour data?')) {
      try {
        await dispatch(deleteHour(id)).unwrap();
        toast.success('Hour data deleted successfully!');
      } catch {
        toast.error('Failed to delete hour data.');
      }
    }
  };

  // Instead of hours array, we have hoursByIct dictionary
  // If selectedICT is set, we show the hours from hoursByIct[selectedICT.id]
  const hourRecords = selectedICT
    ? hoursByIct[selectedICT.id] || []
    : [];

  // --- ICT COST Panel ---
  const handleInsertIctCost = async (e) => {
    e.preventDefault();

    // Convert form strings to numbers
    const payload = {
      ictid: parseInt(newIctCost.ictid, 10),
      ictMrp: parseFloat(newIctCost.ictMrp) || 0,
      cgst: parseFloat(newIctCost.cgst) || 0,
      sgst: parseFloat(newIctCost.sgst) || 0,
      igst: parseFloat(newIctCost.igst) || 0,
      sellPrice: parseFloat(newIctCost.sellPrice) || 0,
    };

    if (!payload.ictid) {
      toast.error('No ICT selected. Cannot insert cost.');
      return;
    }
    if (!payload.ictMrp || !payload.cgst || !payload.sgst || !payload.sellPrice) {
      toast.error('Please fill in all required cost fields: (MRP, CGST, SGST, SellPrice).');
      return;
    }

    try {
      await dispatch(insertIctCost(payload)).unwrap();
      toast.success('ICT Cost inserted successfully!');
      // Clear the form
      setNewIctCost({
        ictid: selectedICT.id,
        ictMrp: '',
        cgst: '',
        sgst: '',
        igst: '',
        sellPrice: '',
      });
      // Re-fetch to show updated list
      dispatch(fetchIctCostByIctId(selectedICT.id));
      setIctCostPopupOpen(false);
    } catch (err) {
      toast.error('Failed to insert ICT Cost.');
    }
  };

  const handleEditIctCost = (cost) => {
    setEditingIctCostId(cost.id);
    setEditingIctCostData({
      ictMrp: String(cost.ictMrp || ''),
      cgst: String(cost.cgst || ''),
      sgst: String(cost.sgst || ''),
      igst: String(cost.igst || ''),
      sellPrice: String(cost.sellPrice || ''),
    });
  };

  const handleSaveIctCost = async () => {
    // Convert editing data to numeric
    const payload = {
      id: editingIctCostId,
      ictid: selectedICT.id,
      ictMrp: parseFloat(editingIctCostData.ictMrp) || 0,
      cgst: parseFloat(editingIctCostData.cgst) || 0,
      sgst: parseFloat(editingIctCostData.sgst) || 0,
      igst: parseFloat(editingIctCostData.igst) || 0,
      sellPrice: parseFloat(editingIctCostData.sellPrice) || 0,
    };

    try {
      await dispatch(updateIctCost(payload)).unwrap();
      toast.success('ICT Cost updated successfully!');
      setEditingIctCostId(null);
      setEditingIctCostData({
        ictMrp: '',
        cgst: '',
        sgst: '',
        igst: '',
        sellPrice: '',
      });
      // Re-fetch to refresh
      dispatch(fetchIctCostByIctId(selectedICT.id));
    } catch {
      toast.error('Failed to update ICT Cost.');
    }
  };

  const handleDeleteIctCost = async (id) => {
    if (window.confirm('Are you sure you want to delete this ICT Cost record?')) {
      try {
        await dispatch(deleteIctCost(id)).unwrap();
        toast.success('ICT Cost deleted successfully!');
      } catch {
        toast.error('Failed to delete ICT Cost.');
      }
    }
  };

  // derived list of costs for selectedICT
  const filteredIctCosts = useMemo(() => {
    if (!selectedICT) return [];
    return ictCosts.filter((c) => Number(c.ictid) === Number(selectedICT.id));
  }, [ictCosts, selectedICT]);

    const getEmpName = (empId) => {
    const emp = employees.find((e) => e.id === empId);
    return emp ? `${emp.empFName} ${emp.empLName}` : empId;
  };

  return (
    <div className="ict-details-container">
      <div className="search-section">
        <h3>Search For Internship / Course / Training Certification</h3>
        <input
          type="text"
          placeholder="Search ICT Data..."
          value={searchTerm}
          onChange={handleSearchChange}
          className="search-input"
        />
        {filteredICTs.length > 0 && (
          <ul className="search-results">
            {filteredICTs.map((ict) => (
              <li
                key={ict.id}
                className="search-result-item"
                onClick={() => handleICTSelect(ict)}
              >
                {ict.ictName}
              </li>
            ))}
          </ul>
        )}
      </div>

      {selectedICT && (
        <div>
          <h3 className="ict-heading">
            Selected ICT: <span>{selectedICT.ictName}</span> | ICT ID: <span>{selectedICT.id}</span>
          </h3>

            <div className="ict-cost-panel">
              {trainersLoading ? (
                <p>Loading trainers…</p>
              ) : trainersError ? (
                <p className="error">{String(trainersError)}</p>
              ) : trainers.length === 0 ? (
                <p></p>
              ) : (
                <div>
                <h3>Trainers</h3>
                <table className="table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Trainer Name</th>
                      <th>Mode</th>
                      <th>Online Cost</th>
                      <th>Classroom Cost</th>
                      <th>FL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trainers.map((t, i) => (
                      <tr key={t.id}>
                        <td>{i + 1}</td>
                        <td>{getEmpName(t.empId)}</td>
                        <td>{t.trainerTrainingMode}</td>
                        <td>{t.trainerCostOnline}</td>
                        <td>{t.trainerCostClassRoom}</td>
                        <td>{t.isFL}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
            
          <div className="content-section ictDetailSection_1">
            {/* STUDY MODES PANEL */}
            <div className="study-modes-panel">
              <h3>
                Study Modes{' '}
                <button
                  className="add-icon"
                  onClick={() => setStudyModePopupOpen(true)}
                >
                  <strong>+</strong>
                </button>
              </h3>
              {studyModesLoading ? (
                <p>Loading study modes...</p>
              ) : (
                <table className="study-modes-table small-tables">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Study Mode</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(studyModes || [])
                      .filter((mode) => mode.ictId_id === selectedICT.id)
                      .map((mode, index) => (
                        <tr key={mode.id}>
                          <td>{index + 1}</td>
                          <td>{mode.studyMode}</td>
                          <td>
                            <label className="switch">
                              <input
                                type="checkbox"
                                checked={mode.status === 'A'}
                                onChange={() => handleStatusToggle(mode.id, mode.status)}
                              />
                              <span className="slider round"></span>
                            </label>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* HOURS PANEL */}
            <div className="hours-panel">
              <h3>
                Hours{' '}
                <button
                  className="add-icon"
                  onClick={() => setHourPopupOpen(true)}
                >
                  <strong>+</strong>
                </button>
              </h3>
              {hoursLoading ? (
                <p>Loading hours...</p>
              ) : (
                <table className="hours-table small-tables">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Training Hour</th>
                      <th>Lab Hour</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Retrieve the array from hoursByIct for this ICT */}
                    {hourRecords.map((hour, index) => (
                      <tr key={hour.id}>
                        <td>{index + 1}</td>
                        <td>
                          {editingHourId === hour.id ? (
                            <input
                              type="number"
                              value={editingHourData.TrainingHour}
                              onChange={(e) =>
                                setEditingHourData({
                                  ...editingHourData,
                                  TrainingHour: e.target.value,
                                })
                              }
                            />
                          ) : (
                            hour.TrainingHour
                          )}
                        </td>
                        <td>
                          {editingHourId === hour.id ? (
                            <input
                              type="number"
                              value={editingHourData.LabHour}
                              onChange={(e) =>
                                setEditingHourData({
                                  ...editingHourData,
                                  LabHour: e.target.value,
                                })
                              }
                            />
                          ) : (
                            hour.LabHour
                          )}
                        </td>
                        <td>
                          {editingHourId === hour.id ? (
                            <i
                              className="fa fa-save action-icon saveIcon"
                              onClick={handleSaveHour}
                            ></i>
                          ) : (
                            <i
                              className="fa fa-edit action-icon editIcon"
                              onClick={() => handleEditHour(hour)}
                            ></i>
                          )}
                          <i
                            className="fa fa-trash action-icon deleteIcon"
                            onClick={() => handleDeleteHour(hour.id)}
                          ></i>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* BENEFITS PANEL */}
          <div className="benefits-panel">
            <h3>
              Benefits{' '}
              <button
                className="add-icon"
                onClick={() => setBenefitPopupOpen(true)}
              >
                  <strong>+</strong>
              </button>
            </h3>
            {benefitsLoading ? (
              <p>Loading benefits...</p>
            ) : (
              <table className="benefits-table small-tables">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Benefit Desc</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(benefits || []).map((benefit, index) => (
                    <tr key={benefit.id}>
                      <td>{index + 1}</td>
                      <td>
                        {editingBenefitId === benefit.id ? (
                          <input
                            type="text"
                            value={editingBenefitDesc}
                            onChange={(e) => setEditingBenefitDesc(e.target.value)}
                          />
                        ) : (
                          benefit.benefitDesc
                        )}
                      </td>
                      <td>
                        {editingBenefitId === benefit.id ? (
                          <i
                            className="fa fa-save action-icon saveIcon"
                            onClick={handleSaveBenefit}
                          ></i>
                        ) : (
                          <i
                            className="fa fa-edit action-icon editIcon"
                            onClick={() => handleEditBenefit(benefit)}
                          ></i>
                        )}
                        <i
                          className="fa fa-trash action-icon deleteIcon"
                          onClick={() => handleDeleteBenefit(benefit.id)}
                        ></i>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ICT COST PANEL */}
          <div className="ict-cost-panel">
            <h3>
              ICT Cost{' '}
              <button
                className="add-icon"
                onClick={() => setIctCostPopupOpen(true)}
              >
                <strong>+</strong>
              </button>
            </h3>
            {ictCostsLoading ? (
              <p>Loading ICT cost...</p>
            ) : ictCostsError ? (
              <p className="error">{String(ictCostsError)}</p>
            ) : (
              <table className="study-modes-table small-tables">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>ICT MRP</th>
                    <th>CGST</th>
                    <th>SGST</th>
                    <th>IGST</th>
                    <th>Sell Price</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredIctCosts.map((cost, index) => (
                    <tr key={cost.id}>
                      <td>{index + 1}</td>
                      <td>
                        {editingIctCostId === cost.id ? (
                          <input
                            type="number"
                            value={editingIctCostData.ictMrp}
                            onChange={(e) =>
                              setEditingIctCostData({
                                ...editingIctCostData,
                                ictMrp: e.target.value,
                              })
                            }
                          />
                        ) : (
                          cost.ictMrp
                        )}
                      </td>
                      <td>
                        {editingIctCostId === cost.id ? (
                          <input
                            type="number"
                            value={editingIctCostData.cgst}
                            onChange={(e) =>
                              setEditingIctCostData({
                                ...editingIctCostData,
                                cgst: e.target.value,
                              })
                            }
                          />
                        ) : (
                          cost.cgst
                        )}
                      </td>
                      <td>
                        {editingIctCostId === cost.id ? (
                          <input
                            type="number"
                            value={editingIctCostData.sgst}
                            onChange={(e) =>
                              setEditingIctCostData({
                                ...editingIctCostData,
                                sgst: e.target.value,
                              })
                            }
                          />
                        ) : (
                          cost.sgst
                        )}
                      </td>
                      <td>
                        {editingIctCostId === cost.id ? (
                          <input
                            type="number"
                            value={editingIctCostData.igst}
                            onChange={(e) =>
                              setEditingIctCostData({
                                ...editingIctCostData,
                                igst: e.target.value,
                              })
                            }
                          />
                        ) : (
                          cost.igst
                        )}
                      </td>
                      <td>
                        {editingIctCostId === cost.id ? (
                          <input
                            type="number"
                            value={editingIctCostData.sellPrice}
                            onChange={(e) =>
                              setEditingIctCostData({
                                ...editingIctCostData,
                                sellPrice: e.target.value,
                              })
                            }
                          />
                        ) : (
                          cost.sellPrice
                        )}
                      </td>
                      <td>
                        {editingIctCostId === cost.id ? (
                          <i
                            className="fa fa-save action-icon saveIcon"
                            onClick={handleSaveIctCost}
                          ></i>
                        ) : (
                          <i
                            className="fa fa-edit action-icon editIcon"
                            onClick={() => handleEditIctCost(cost)}
                          ></i>
                        )}
                        <i
                          className="fa fa-trash action-icon deleteIcon"
                          onClick={() => handleDeleteIctCost(cost.id)}
                        ></i>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Popup for Insert Hour */}
      {isHourPopupOpen && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>Insert Hour</h3>
            <form onSubmit={handleInsertHour}>
              <div className="form-group">
                <label>Training Hour</label>
                <input
                  type="number"
                  placeholder="Must Be Positive & Whole Number"
                  value={newHour.TrainingHour}
                  onChange={(e) =>
                    setNewHour({ ...newHour, TrainingHour: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>Lab Hour</label>
                <input
                  type="number"
                  placeholder="Must Be Positive & Whole Number"
                  value={newHour.LabHour}
                  onChange={(e) =>
                    setNewHour({ ...newHour, LabHour: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="fa fa-check submit-icon"></button>
                <button
                  type="button"
                  className="fa fa-times cancel-icon"
                  onClick={() => setHourPopupOpen(false)}
                ></button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Popup for Insert Study Mode */}
      {isStudyModePopupOpen && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>Insert Study Mode</h3>
            <form onSubmit={handleInsertStudyMode}>
              <div className="form-group">
                <label>Study Mode</label>
                <input
                  type="text"
                  placeholder="Online / Class Room"
                  value={newStudyMode.studyMode}
                  onChange={(e) =>
                    setNewStudyMode({ ...newStudyMode, studyMode: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  placeholder="Optional"
                  value={newStudyMode.studyModeDesc}
                  onChange={(e) =>
                    setNewStudyMode({ ...newStudyMode, studyModeDesc: e.target.value })
                  }
                  required
                ></textarea>
              </div>
              <div className="form-actions">
                <button type="submit" className="submit-btn fa fa-check"></button>
                <button
                  type="button"
                  className="cancel-btn fa fa-close"
                  onClick={() => setStudyModePopupOpen(false)}
                ></button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Popup for Insert Benefit */}
      {isBenefitPopupOpen && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>Insert Benefit</h3>
            <form onSubmit={handleInsertBenefit}>
              <div className="form-group">
                <label>Benefit Description</label>
                <textarea
                  placeholder="Keep It In 1-2 Sentences"
                  value={newBenefit.benefitDesc}
                  onChange={(e) =>
                    setNewBenefit({ ...newBenefit, benefitDesc: e.target.value })
                  }
                  required
                ></textarea>
              </div>
              <div className="form-actions">
                <button type="submit" className="submit-btn fa fa-check"></button>
                <button
                  type="button"
                  className="cancel-btn fa fa-close"
                  onClick={() => setBenefitPopupOpen(false)}
                ></button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Popup for Insert ICT Cost */}
      {isIctCostPopupOpen && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>Insert ICT Cost</h3>
            <form onSubmit={handleInsertIctCost}>
              <div className="form-group">
                <label>ICT MRP</label>
                <input
                  type="number"
                  value={newIctCost.ictMrp}
                  onChange={(e) =>
                    setNewIctCost({ ...newIctCost, ictMrp: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>CGST</label>
                <input
                  type="number"
                  value={newIctCost.cgst}
                  onChange={(e) =>
                    setNewIctCost({ ...newIctCost, cgst: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>SGST</label>
                <input
                  type="number"
                  value={newIctCost.sgst}
                  onChange={(e) =>
                    setNewIctCost({ ...newIctCost, sgst: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>IGST</label>
                <input
                  type="number"
                  value={newIctCost.igst}
                  onChange={(e) =>
                    setNewIctCost({ ...newIctCost, igst: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>Sell Price</label>
                <input
                  type="number"
                  value={newIctCost.sellPrice}
                  onChange={(e) =>
                    setNewIctCost({ ...newIctCost, sellPrice: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="submit-btn fa fa-check"></button>
                <button
                  type="button"
                  className="cancel-btn fa fa-close"
                  onClick={() => setIctCostPopupOpen(false)}
                ></button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
};

export default ICTDetails;
